import type { SxProps, Theme } from '@mui/material';

export const WEBVIEWER_LICENSE =
	'demo:1770900361581:6082a73403000000008bfdc578db599d0904b024b2339e144c1e745ec5';

export type WebViewerToolbarProfile = 'formFill' | 'signOnly' | 'viewOnly';

/** Default document zoom (150%) applied when a PDF loads. */
export const DEFAULT_WEBVIEWER_ZOOM = 1.5;

const COMMON_HIDDEN_TOOLBAR_GROUPS = [
	'toolbarGroup-Annotate',
	'toolbarGroup-Shapes',
	'toolbarGroup-Measure',
	'toolbarGroup-Edit',
	'toolbarGroup-EditText',
	'toolbarGroup-Forms',
	'toolbarGroup-Redact',
	'toolbarGroup-oe-Home',
	'toolbarGroup-oe-Insert',
	'toolbarGroup-oe-Layout',
	'toolbarGroup-oe-Review',
];

const PROFILE_UI: Record<
	WebViewerToolbarProfile,
	{
		disable: string[];
		enable: string[];
		toolbarGroup: string;
		toolMode: string;
	}
> = {
	formFill: {
		disable: COMMON_HIDDEN_TOOLBAR_GROUPS,
		enable: ['toolbarGroup-View', 'toolbarGroup-Insert', 'toolbarGroup-FillAndSign'],
		toolbarGroup: 'toolbarGroup-View',
		toolMode: 'Pan',
	},
	signOnly: {
		disable: [
			...COMMON_HIDDEN_TOOLBAR_GROUPS,
			'toolbarGroup-View',
			'viewControlsButton',
			'searchButton',
			'menuButton',
			'leftPanelButton',
			'toggleNotesButton',
		],
		enable: [
			'toolbarGroup-FillAndSign',
			'signatureToolButton',
			'freeHandToolButton',
			'freeTextToolButton',
		],
		toolbarGroup: 'toolbarGroup-FillAndSign',
		toolMode: 'Pan',
	},
	viewOnly: {
		disable: [
			...COMMON_HIDDEN_TOOLBAR_GROUPS,
			'toolbarGroup-Insert',
			'toolbarGroup-FillAndSign',
			'viewControlsButton',
			'searchButton',
			'menuButton',
			'leftPanelButton',
			'toggleNotesButton',
		],
		enable: ['toolbarGroup-View'],
		toolbarGroup: 'toolbarGroup-View',
		toolMode: 'Pan',
	},
};

export function getWebViewerInitOptions(
	profile: WebViewerToolbarProfile,
	documentUrl?: string
): Record<string, unknown> {
	return {
		path: '/webviewer',
		licenseKey: WEBVIEWER_LICENSE,
		initialDoc: documentUrl || undefined,
		fullAPI: true,
		disableStreaming: true,
		disabledElements: PROFILE_UI[profile].disable,
	};
}

export function configureWebViewerUI(inst: any, profile: WebViewerToolbarProfile): void {
	const { disable, enable, toolbarGroup, toolMode } = PROFILE_UI[profile];
	inst.UI.disableElements(disable);
	inst.UI.enableElements(enable);
	inst.UI.setToolbarGroup(toolbarGroup);
	inst.UI.setToolMode(toolMode);
}

/** PDF open actions can force FitPage and override our zoom — ignore those on document open. */
export function ignoreDocumentOpenFitActions(inst: any): void {
	if (inst.__openActionPatchApplied) return;

	try {
		const { documentViewer } = inst.Core;
		const Actions = inst.Core?.Actions ?? inst.Actions;
		if (!Actions?.GoTo?.prototype?.onTriggered) return;

		const originalOnTriggered = Actions.GoTo.prototype.onTriggered;
		Actions.GoTo.prototype.onTriggered = function onTriggeredPatched(
			this: unknown,
			target: unknown,
			event: { name?: string },
			...args: unknown[]
		) {
			if (target === documentViewer.getDocument() && event?.name === 'Open') {
				return;
			}
			return originalOnTriggered.apply(this, [target, event, ...args]);
		};
		inst.__openActionPatchApplied = true;
	} catch {
		/* non-fatal */
	}
}

export function applyWebViewerDefaultZoom(inst: any): void {
	const { UI } = inst;
	const { documentViewer } = inst.Core;

	UI.setFitMode(UI.FitMode.Zoom);
	if (documentViewer?.setFitMode && documentViewer.FitMode?.Zoom) {
		documentViewer.setFitMode(documentViewer.FitMode.Zoom);
	}
	UI.setZoomLevel(DEFAULT_WEBVIEWER_ZOOM);
}

export function applyWebViewerDefaultTool(inst: any, profile: WebViewerToolbarProfile): void {
	inst.UI.setToolMode(PROFILE_UI[profile].toolMode);
}

function scheduleDefaultView(inst: any, profile: WebViewerToolbarProfile): void {
	const applyView = () => {
		try {
			applyWebViewerDefaultZoom(inst);
			applyWebViewerDefaultTool(inst, profile);
		} catch (error) {
			console.error('[WebViewer] Failed to apply default view:', error);
		}
	};

	const onFitModeUpdated = () => {
		try {
			if (inst.UI.getFitMode?.() !== inst.UI.FitMode.Zoom) {
				applyView();
			}
		} catch {
			/* non-fatal */
		}
	};

	inst.Core.documentViewer.addEventListener('fitModeUpdated', onFitModeUpdated);
	window.setTimeout(() => {
		inst.Core.documentViewer.removeEventListener('fitModeUpdated', onFitModeUpdated);
	}, 2000);

	applyView();
	requestAnimationFrame(applyView);
	[100, 300, 600, 1000].forEach((delayMs) => window.setTimeout(applyView, delayMs));
}

export function setupWebViewerOnDocumentLoaded(inst: any, profile: WebViewerToolbarProfile): void {
	configureWebViewerUI(inst, profile);
	scheduleDefaultView(inst, profile);
}

/** Call as soon as the WebViewer instance is created (before or after the first document loads). */
export function prepareWebViewerInstance(inst: any, profile: WebViewerToolbarProfile): void {
	ignoreDocumentOpenFitActions(inst);
	configureWebViewerUI(inst, profile);
}

/** @deprecated Use applyWebViewerDefaultZoom */
export function applyWebViewerSinglePageView(inst: any): void {
	applyWebViewerDefaultZoom(inst);
}

/** Fixed height sized to show roughly one PDF page — avoids full-viewport editors that break iPad scroll/sign dialogs. */
export function getWebViewerHeight(
	isTabletViewport: boolean,
	variant: 'inline' | 'modal' = 'inline'
): string {
	if (variant === 'modal') {
		return isTabletViewport ? '480px' : '560px';
	}
	return isTabletViewport ? '500px' : '600px';
}

export function getWebViewerContainerSx(
	isTabletViewport: boolean,
	variant: 'inline' | 'modal' = 'inline'
): SxProps<Theme> {
	return {
		height: getWebViewerHeight(isTabletViewport, variant),
		width: '100%',
		border: '1px solid #e0e0e0',
		borderRadius: 1,
		overflow: 'hidden',
		position: 'relative',
		touchAction: 'manipulation',
		WebkitOverflowScrolling: 'touch',
	};
}
